/**
 * TipTap Rich Text Editor for tracker notes.
 *
 * Stores notes as **markdown** (round-tripped via tiptap-markdown). Existing
 * plain-text notes are valid markdown so no migration is needed. Whitespace —
 * including paragraph breaks, soft line breaks, and trailing two-space hard
 * breaks — is preserved end-to-end; we never trim or normalize the editor
 * output.
 *
 * UX is adaptive:
 * - At `>= md` we hide a fixed toolbar and surface formatting via a floating
 *   bubble menu on text selection (Medium-style).
 * - At `< md` we show a sticky bottom toolbar that sits above the soft
 *   keyboard with `env(safe-area-inset-bottom)` padding for iOS.
 * - Both surfaces share a slash menu (`/`) for inserting blocks. The
 *   popover is positioned above the cursor so it never lands behind the
 *   on-screen keyboard.
 *
 * Typography: serif body stack + `prose` width + generous leading, matching
 * the writing surface to the long-form intent of a daily entry.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { EditorContent, type Editor, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown, type MarkdownStorage } from "tiptap-markdown";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Code,
  Minus,
  CodeSquare,
} from "lucide-react";
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SlashCommand, type SlashCommandItem } from "./slashCommand";

interface TipTapEditorProps {
  /** Markdown content. Treated as the source of truth on initial mount and
   *  whenever the parent passes a new value (e.g. switching entries). */
  content: string;
  /** Called with markdown on every change. Whitespace is preserved verbatim. */
  onChange: (markdown: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

const SLASH_COMMANDS: SlashCommandItem[] = [
  {
    title: "Heading 2",
    description: "Big section heading",
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 2 })
        .run();
    },
  },
  {
    title: "Heading 3",
    description: "Smaller section heading",
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 3 })
        .run();
    },
  },
  {
    title: "Bullet list",
    description: "An unordered list",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: "Numbered list",
    description: "An ordered list",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: "Quote",
    description: "Pull-quote / blockquote",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    title: "Divider",
    description: "Horizontal rule",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },
  {
    title: "Code block",
    description: "Multi-line code",
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
];

interface SlashState {
  open: boolean;
  query: string;
  range: { from: number; to: number } | null;
  clientRect: (() => DOMRect | null) | null;
  items: SlashCommandItem[];
  selectedIndex: number;
}

const INITIAL_SLASH_STATE: SlashState = {
  open: false,
  query: "",
  range: null,
  clientRect: null,
  items: [],
  selectedIndex: 0,
};

/** tiptap-markdown's storage isn't merged into TipTap's Editor.storage type. */
function getMarkdownStorage(editor: Editor): MarkdownStorage {
  return (editor.storage as unknown as { markdown: MarkdownStorage }).markdown;
}

function filterCommands(query: string): SlashCommandItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter(
    (c) =>
      c.title.toLowerCase().includes(q) ||
      (c.description?.toLowerCase().includes(q) ?? false)
  );
}

export function TipTapEditor({
  content,
  onChange,
  placeholder = "How are you feeling about your relationship today?",
  className,
  minHeight = "240px",
}: TipTapEditorProps) {
  const [slash, setSlash] = useState<SlashState>(INITIAL_SLASH_STATE);
  const slashRef = useRef<SlashState>(INITIAL_SLASH_STATE);
  slashRef.current = slash;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit ships heading/blockquote/code/codeBlock/lists already.
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "underline underline-offset-4",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Markdown.configure({
        // Match how a writer would actually format markdown by hand. We do NOT
        // enable transformPastedText — pasting markdown should remain literal.
        html: false,
        tightLists: true,
        breaks: true,
        linkify: true,
        transformPastedText: false,
        transformCopiedText: false,
      }),
      Placeholder.configure({
        placeholder,
        // Only show on a truly empty doc (avoids the placeholder leaking
        // onto every empty paragraph the user creates inside an entry).
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
        emptyEditorClass: "is-editor-empty",
      }),
      SlashCommand.configure({
        suggestion: {
          items: ({ query }) => filterCommands(query),
          render: () => {
            // The Suggestion plugin invokes its `command` option to apply a
            // selection. v3 doesn't surface that on SuggestionKeyDownProps, so
            // we close over `props.command` from onStart/onUpdate instead.
            let commandRef: ((item: SlashCommandItem) => void) | null = null;

            return {
              onStart: (props) => {
                commandRef = props.command;
                setSlash({
                  open: true,
                  query: props.query,
                  range: props.range,
                  clientRect: props.clientRect ?? null,
                  items: props.items,
                  selectedIndex: 0,
                });
              },
              onUpdate: (props) => {
                commandRef = props.command;
                setSlash((prev) => ({
                  ...prev,
                  open: true,
                  query: props.query,
                  range: props.range,
                  clientRect: props.clientRect ?? null,
                  items: props.items,
                  selectedIndex: Math.min(
                    prev.selectedIndex,
                    Math.max(0, props.items.length - 1)
                  ),
                }));
              },
              onKeyDown: (props) => {
                const state = slashRef.current;
                if (!state.open) return false;
                if (props.event.key === "ArrowDown") {
                  setSlash((prev) => ({
                    ...prev,
                    selectedIndex:
                      (prev.selectedIndex + 1) % Math.max(prev.items.length, 1),
                  }));
                  return true;
                }
                if (props.event.key === "ArrowUp") {
                  setSlash((prev) => ({
                    ...prev,
                    selectedIndex:
                      (prev.selectedIndex -
                        1 +
                        Math.max(prev.items.length, 1)) %
                      Math.max(prev.items.length, 1),
                  }));
                  return true;
                }
                if (props.event.key === "Enter") {
                  const item = state.items[state.selectedIndex];
                  if (item && commandRef) {
                    commandRef(item);
                    return true;
                  }
                }
                if (props.event.key === "Escape") {
                  setSlash(INITIAL_SLASH_STATE);
                  return true;
                }
                return false;
              },
              onExit: () => {
                commandRef = null;
                setSlash(INITIAL_SLASH_STATE);
              },
            };
          },
        },
      }),
    ],
    content: content || "",
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-base max-w-none focus:outline-none",
          // Serif typography matches the long-form intent of a tracker entry.
          "font-serif",
          // Comfortable measure + leading for a writing surface.
          "max-w-prose mx-auto leading-relaxed",
          // Prose plugin sets heading sizes; nudge spacing for tighter blocks.
          "prose-p:my-3 prose-headings:font-serif prose-blockquote:border-l-4",
          "px-4 py-6 sm:px-6 sm:py-8"
        ),
        style: `min-height: ${minHeight}`,
      },
    },
    onUpdate: ({ editor }) => {
      // tiptap-markdown is the source of truth. Do NOT trim — we must preserve
      // every newline and trailing two-space hard break the user produced.
      const md = getMarkdownStorage(editor).getMarkdown();
      onChange(md);
    },
  });

  // Sync external content changes (e.g. parent loads a different entry).
  useEffect(() => {
    if (!editor) return;
    const current: string = getMarkdownStorage(editor).getMarkdown();
    if (content !== current) {
      editor.commands.setContent(content || "", { emitUpdate: false });
    }
  }, [content, editor]);

  if (!editor) {
    return (
      <div
        className={cn(
          "border rounded-md overflow-hidden h-[240px] animate-pulse bg-muted/30",
          className
        )}
      />
    );
  }

  return (
    <div className={cn("relative", className)}>
      {/* Bubble menu (desktop only) */}
      <div className="hidden md:block">
        <BubbleMenu
          editor={editor}
          options={{ placement: "top" }}
          className="flex items-center gap-1 rounded-md border bg-popover p-1 shadow-md"
        >
          <ToolbarButton
            icon={Bold}
            label="Bold"
            isActive={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <ToolbarButton
            icon={Italic}
            label="Italic"
            isActive={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <LinkButton editor={editor} />
          <Divider />
          <ToolbarButton
            icon={Heading2}
            label="Heading 2"
            isActive={editor.isActive("heading", { level: 2 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />
          <ToolbarButton
            icon={Heading3}
            label="Heading 3"
            isActive={editor.isActive("heading", { level: 3 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          />
          <ToolbarButton
            icon={Quote}
            label="Quote"
            isActive={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          />
          <ToolbarButton
            icon={Code}
            label="Inline code"
            isActive={editor.isActive("code")}
            onClick={() => editor.chain().focus().toggleCode().run()}
          />
        </BubbleMenu>
      </div>

      <EditorContent editor={editor} />

      {/* Mobile toolbar (sticky bottom) */}
      <div className="md:hidden">
        <MobileFormatBar editor={editor} />
      </div>

      {slash.open ? (
        <SlashMenu
          state={slash}
          onSelect={(item) => {
            if (slash.range) {
              item.command({ editor, range: slash.range });
            }
          }}
        />
      ) : null}
    </div>
  );
}

interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isActive?: boolean;
  onClick: () => void;
}

function ToolbarButton({
  icon: Icon,
  label,
  isActive,
  onClick,
}: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn("h-8 w-8 p-0", isActive && "bg-accent")}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function Divider() {
  return <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-border" />;
}

function LinkButton({ editor }: { editor: Editor }) {
  return (
    <ToolbarButton
      icon={LinkIcon}
      label="Link"
      isActive={editor.isActive("link")}
      onClick={() => {
        const previous = editor.getAttributes("link").href as
          | string
          | undefined;
        const url = window.prompt("URL", previous ?? "");
        if (url === null) return; // cancel
        if (url === "") {
          editor.chain().focus().extendMarkRange("link").unsetLink().run();
          return;
        }
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      }}
    />
  );
}

function MobileFormatBar({ editor }: { editor: Editor }) {
  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-30 flex items-center gap-1 overflow-x-auto",
        "border-t bg-background px-2 py-1 shadow-sm",
        // Respect iOS safe area so the bar sits above the home indicator.
        "pb-[max(0.25rem,env(safe-area-inset-bottom))]"
      )}
      // Prevent the toolbar tap from blurring the editor (which would close
      // the soft keyboard and re-position everything).
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <ToolbarButton
        icon={Bold}
        label="Bold"
        isActive={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={Italic}
        label="Italic"
        isActive={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <LinkButton editor={editor} />
      <Divider />
      <ToolbarButton
        icon={Heading2}
        label="Heading 2"
        isActive={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        icon={Heading3}
        label="Heading 3"
        isActive={editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />
      <ToolbarButton
        icon={Quote}
        label="Quote"
        isActive={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolbarButton
        icon={Code}
        label="Inline code"
        isActive={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />
      <Divider />
      <ToolbarButton
        icon={List}
        label="Bullet list"
        isActive={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={ListOrdered}
        label="Numbered list"
        isActive={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        icon={Minus}
        label="Divider"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />
      <ToolbarButton
        icon={CodeSquare}
        label="Code block"
        isActive={editor.isActive("codeBlock")}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
    </div>
  );
}

interface SlashMenuProps {
  state: SlashState;
  onSelect: (item: SlashCommandItem) => void;
}

function SlashMenu({ state, onSelect }: SlashMenuProps) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);

  // Position the popover above the cursor (so the soft keyboard can't cover
  // it on mobile) using Floating UI. Recompute on scroll/resize.
  useLayoutEffect(() => {
    const popEl = popRef.current;
    const getRect = state.clientRect;
    if (!popEl || !getRect) return;

    const referenceEl = {
      getBoundingClientRect: () => getRect() ?? new DOMRect(0, 0, 0, 0),
    };

    const update = async () => {
      const result = await computePosition(referenceEl, popEl, {
        placement: "top-start",
        middleware: [offset(8), flip(), shift({ padding: 8 })],
      });
      setCoords({ x: result.x, y: result.y });
    };

    update();
    return autoUpdate(referenceEl, popEl, update);
  }, [state.clientRect]);

  if (!state.open || state.items.length === 0) return null;

  return (
    <div
      ref={popRef}
      style={{
        position: "fixed",
        top: coords?.y ?? -9999,
        left: coords?.x ?? -9999,
        zIndex: 50,
      }}
      role="listbox"
      aria-label="Insert block"
      className="w-64 overflow-hidden rounded-md border bg-popover shadow-lg"
    >
      <ul className="max-h-64 overflow-y-auto py-1">
        {state.items.map((item, idx) => (
          <li key={item.title}>
            <button
              type="button"
              role="option"
              aria-selected={idx === state.selectedIndex}
              className={cn(
                "flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                idx === state.selectedIndex && "bg-accent"
              )}
              onMouseDown={(e) => {
                // Prevent editor blur — keep the cursor in place for command.
                e.preventDefault();
                onSelect(item);
              }}
            >
              <span className="flex flex-col">
                <span className="font-medium">{item.title}</span>
                {item.description ? (
                  <span className="text-xs text-muted-foreground">
                    {item.description}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
